import { httpClient } from '../http-client';
import { ApiResponse } from '../../types/responses/api-response';
import type { SupportGetSupportAnalyticsData } from '../../api/generated/types.gen';

/**
 * AD-137. Параметры `GET /support/analytics` — ровно то, что принимает
 * `SupportAnalyticsDto`: тип взят из сгенерированного контракта, а не написан
 * руками. Прежняя подпись обещала `from`/`to`, и сервер с `forbidNonWhitelisted`
 * отвечал на них 400 целым запросом.
 */
export type SupportAnalyticsQuery = NonNullable<
  SupportGetSupportAnalyticsData['query']
>;

export interface SupportTicket {
  id: string;
  title: string;
  description: string;
  status: 'open' | 'in_progress' | 'resolved' | 'closed';
  priority: 'low' | 'medium' | 'high' | 'critical';
  categoryId?: string;
  categoryName?: string;
  userId: string;
  userName: string;
  userEmail: string;
  assigneeId?: string;
  assigneeName?: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt?: string;
}

export interface SupportReply {
  id: string;
  ticketId: string;
  userId: string;
  userName: string;
  content: string;
  isInternal: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SupportCategory {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  ticketCount?: number;
}

export interface SupportAnalytics {
  stats: {
    totalTickets: number;
    openTickets: number;
    resolvedTickets: number;
    averageResponseTime: number;
    averageResolutionTime: number;
    satisfactionRate: number;
  };
  trends: {
    ticketsByDate: Array<{ date: string; count: number }>;
    ticketsByCategory: Array<{ category: string; count: number }>;
    ticketsByPriority: Array<{ priority: string; count: number }>;
  };
  performance: {
    agentStats: Array<{
      agentId: string;
      agentName: string;
      ticketsHandled: number;
      averageResponseTime: number;
      satisfactionRate: number;
    }>;
  };
}

export const supportApi = {
  // ==================== TICKETS ====================
  
  /**
   * Получить список тикетов
   */
  getTickets: async (params?: { 
    status?: string; 
    priority?: string;
    categoryId?: string; 
    assigneeId?: string;
    search?: string;
    page?: number;
    limit?: number;
  }): Promise<ApiResponse<{
    tickets: SupportTicket[];
    total: number;
    page: number;
    limit: number;
  }>> => {
    return httpClient.get('/support/tickets', { params });
  },

  /**
   * Получить тикет по ID
   */
  getTicket: async (id: string): Promise<ApiResponse<SupportTicket>> => {
    return httpClient.get<SupportTicket>(`/support/tickets/${id}`);
  },

  /**
   * Создать тикет
   */
  createTicket: async (data: Partial<SupportTicket>): Promise<ApiResponse<SupportTicket>> => {
    return httpClient.post<SupportTicket>('/support/tickets', data);
  },

  /**
   * Обновить тикет
   */
  updateTicket: async (id: string, data: Partial<SupportTicket>): Promise<ApiResponse<SupportTicket>> => {
    return httpClient.put<SupportTicket>(`/support/tickets/${id}`, data);
  },

  /**
   * Удалить тикет
   */
  deleteTicket: async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
    return httpClient.delete(`/support/tickets/${id}`);
  },

  /**
   * Закрыть тикет
   */
  closeTicket: async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
    return httpClient.post(`/support/tickets/${id}/close`);
  },

  // ==================== REPLIES ====================

  /**
   * Получить ответы на тикет
   */
  getReplies: async (ticketId: string): Promise<ApiResponse<{
    replies: SupportReply[];
    total: number;
  }>> => {
    return httpClient.get(`/support/tickets/${ticketId}/replies`);
  },

  /**
   * Добавить ответ
   */
  addReply: async (ticketId: string, content: string, isInternal: boolean = false): Promise<ApiResponse<SupportReply>> => {
    return httpClient.post<SupportReply>(`/support/tickets/${ticketId}/replies`, { 
      content,
      isInternal 
    });
  },

  /**
   * Обновить ответ
   */
  updateReply: async (replyId: string, content: string): Promise<ApiResponse<SupportReply>> => {
    return httpClient.put<SupportReply>(`/support/replies/${replyId}`, { content });
  },

  /**
   * Удалить ответ
   */
  deleteReply: async (replyId: string): Promise<ApiResponse<{ success: boolean }>> => {
    return httpClient.delete(`/support/replies/${replyId}`);
  },

  // ==================== CATEGORIES ====================

  /**
   * Получить категории
   */
  getCategories: async (): Promise<ApiResponse<SupportCategory[]>> => {
    return httpClient.get<SupportCategory[]>('/support/categories');
  },

  /**
   * Создать категорию
   */
  createCategory: async (data: Partial<SupportCategory>): Promise<ApiResponse<SupportCategory>> => {
    return httpClient.post<SupportCategory>('/support/categories', data);
  },

  /**
   * Обновить категорию
   */
  updateCategory: async (id: string, data: Partial<SupportCategory>): Promise<ApiResponse<SupportCategory>> => {
    return httpClient.put<SupportCategory>(`/support/categories/${id}`, data);
  },

  /**
   * Удалить категорию
   */
  deleteCategory: async (id: string): Promise<ApiResponse<{ success: boolean }>> => {
    return httpClient.delete(`/support/categories/${id}`);
  },

  // ==================== ANALYTICS ====================

  /**
   * Получить общую статистику
   */
  getAnalyticsStats: async (): Promise<ApiResponse<SupportAnalytics['stats']>> => {
    return httpClient.get('/support/analytics/stats');
  },

  /**
   * Получить тренды
   */
  getAnalyticsTrends: async (params?: { from?: string; to?: string }): Promise<ApiResponse<SupportAnalytics['trends']>> => {
    return httpClient.get('/support/analytics/trends', { params });
  },

  /**
   * Получить производительность агентов
   */
  getAnalyticsPerformance: async (): Promise<ApiResponse<SupportAnalytics['performance']>> => {
    return httpClient.get('/support/analytics/performance');
  },

  /**
   * Получить полную аналитику
   */
  getAnalytics: async (params?: SupportAnalyticsQuery): Promise<ApiResponse<SupportAnalytics>> => {
    return httpClient.get<SupportAnalytics>('/support/analytics', { params });
  },
};

export default supportApi;


